import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import { toast } from "react-toastify";
import ReceiptModal from "./ReceiptModal";

// ── Group bank-upload receipts ────────────────────────────────────────────────
// Priority 1: records with batchId  → group by batchId  (new batch system)
// Priority 2: old BANK records with same transactionId → group by transactionId
// Everything else (Razorpay, cash, unique txn) → kept as standalone cards
function groupReceipts(raw) {
  const standalone = [];
  const batchMap   = {};   // batchId._id  → merged card
  const txnMap     = {};   // transactionId → merged card (old BANK records)

  const mergeInto = (group, r) => {
    (r.vfcIds || []).forEach(v => group.vfcIds.push(v));
    (r.vfcIds || []).forEach(v => {
      group.vfcReceipts.push({ vfcId: v._id || v, vfcName: v.vfcName || "VFC", invoiceNo: r.invoiceNo });
    });
    group.totalAmount += (r.totalAmount || 0);
    group.amountPaid  += (r.amountPaid  || 0);
    group.gstAmount   += (r.gstAmount   || 0);
  };

  raw.forEach(r => {
    const batch = r.batchId;

    if (batch && batch._id) {
      // ── New batch system ──
      const bid = batch._id.toString();
      if (!batchMap[bid]) {
        batchMap[bid] = {
          _id:           bid,
          _isBatch:      true,
          batchRef:      batch.batchRef,
          rangeId:       r.rangeId,
          vfcIds:        [],
          vfcReceipts:   [],
          totalAmount:   0,
          amountPaid:    0,
          gstAmount:     0,
          paymentMethod: "BANK",
          invoiceNo:     batch.batchRef,
          invoiceNos:    batch.invoiceNos || [],
          paidOn:        batch.transactionDate || r.paidOn,
          financialYear: r.financialYear,
          status:        "SUCCESS",
        };
      }
      mergeInto(batchMap[bid], r);

    } else if (r.paymentMethod === "BANK" && r.transactionId) {
      // ── Old BANK records — group by transactionId ──
      const txnKey = r.transactionId;
      if (!txnMap[txnKey]) {
        txnMap[txnKey] = {
          _id:           `txn_${txnKey}`,
          _isTxnGroup:   true,
          rangeId:       r.rangeId,
          vfcIds:        [],
          vfcReceipts:   [],
          totalAmount:   0,
          amountPaid:    0,
          gstAmount:     0,
          paymentMethod: "BANK",
          transactionId: r.transactionId,
          invoiceNo:     r.invoiceNo,
          paidOn:        r.paidOn,
          financialYear: r.financialYear,
          status:        r.status,
        };
      }
      mergeInto(txnMap[txnKey], r);

    } else {
      standalone.push(r);
    }
  });

  // Flatten txnMap: single-record groups unwrap back to individual cards
  const txnGroups = Object.values(txnMap).map(g =>
    g.vfcIds.length === 1 ? { ...g, vfcReceipts: [] } : g
  );

  return [...standalone, ...Object.values(batchMap), ...txnGroups].sort((a, b) =>
    new Date(b.paidOn || 0) - new Date(a.paidOn || 0)
  );
}

/* ── Financial year helpers ── */
const getCurrentFY = () => {
  const now  = new Date();
  const yr   = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${yr}-${yr + 1}`;
};

const PER_VFC_AMOUNT = 350;
const GST            = 0.18;
const TOTAL_PER_VFC  = Math.round(PER_VFC_AMOUNT + PER_VFC_AMOUNT * GST);

const inputStyle = {
  width: "100%", padding: "9px 12px",
  border: "1px solid #E2E8F0", borderRadius: 8,
  fontSize: 13, color: "#2D3748", background: "#fff",
  outline: "none", transition: "border-color 0.15s", boxSizing: "border-box",
};
const labelStyle = {
  fontSize: 12, fontWeight: 600, color: "#4A5568",
  marginBottom: 6, display: "block",
};

/* ── Stat card ── */
const StatCard = ({ icon, label, value, accent, sub }) => (
  <div style={{
    background: "#fff", border: "1px solid #E2E8F0",
    borderRadius: 12, padding: "16px 20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    borderTop: `3px solid ${accent}`,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 9,
        background: `${accent}18`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`mdi mdi-${icon}`} style={{ color: accent, fontSize: 18 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color: "#1A202C" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#A0AEC0", marginTop: 3 }}>{sub}</div>}
  </div>
);

/* ── Receipt card ── */
const ReceiptCard = ({ receipt, onView }) => {
  const [expanded, setExpanded] = useState(false);

  const date     = receipt.paidOn
    ? new Date(receipt.paidOn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";
  const vfcNames      = receipt.vfcIds?.map(v => v.vfcName || v).join(", ") || "—";
  const perVfcList    = receipt.vfcReceipts || [];
  const vfcCount      = receipt.vfcIds?.length || 1;
  const perVfcTotal   = Math.round((receipt.totalAmount || 0) / vfcCount);

  const ViewBtn = ({ label = "View Receipt", small = false, onClick }) => (
    <button onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: small ? 4 : 6,
      padding: small ? "5px 10px" : "7px 14px",
      background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
      color: "#fff", border: "none", borderRadius: 7,
      fontSize: small ? 11 : 12, fontWeight: 700, cursor: "pointer",
      boxShadow: "0 2px 6px rgba(46,125,50,0.25)", whiteSpace: "nowrap",
    }}>
      <i className="mdi mdi-eye" style={{ fontSize: small ? 13 : 15 }} />
      {label}
    </button>
  );

  return (
    <div style={{
      background: "#fff", border: "1px solid #E2E8F0",
      borderRadius: 12, overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    }}>
      {/* ── Main row ── */}
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10, flexShrink: 0,
          background: "#E8F5E9", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <i className="mdi mdi-receipt" style={{ color: "#2E7D32", fontSize: 20 }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>
              {receipt.rangeId?.rangeName || "—"}
            </div>
            {receipt._isBatch && (
              <span style={{
                background: "#EFF6FF", color: "#1D4ED8",
                fontSize: 10, fontWeight: 700, borderRadius: 10,
                padding: "1px 8px", border: "1px solid #BFDBFE",
              }}>{receipt.batchRef}</span>
            )}
            {receipt._isTxnGroup && (
              <span style={{
                background: "#F5F3FF", color: "#6D28D9",
                fontSize: 10, fontWeight: 700, borderRadius: 10,
                padding: "1px 8px", border: "1px solid #DDD6FE",
              }}>TXN: {receipt.transactionId}</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#718096", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {vfcNames}
          </div>
          <div style={{ fontSize: 11, color: "#A0AEC0", marginTop: 2 }}>
            {date} &nbsp;·&nbsp;
            <span style={{
              background: receipt.paymentMethod === "RAZORPAY" ? "#EBF8FF" : "#FAF5FF",
              color:      receipt.paymentMethod === "RAZORPAY" ? "#2B6CB0" : "#553C9A",
              padding: "1px 7px", borderRadius: 10, fontWeight: 600, fontSize: 10,
            }}>{receipt.paymentMethod}</span>
            &nbsp;·&nbsp;{vfcCount} VFC(s)
          </div>
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1B5E20" }}>
            ₹ {Number(receipt.totalAmount || 0).toLocaleString("en-IN")}
          </div>
          <div style={{ fontSize: 10, color: "#A0AEC0", marginTop: 2 }}>GST incl.</div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <ViewBtn label="View Receipt" onClick={() => {
            // Single VFC → open as individual VFC receipt (same format as Per-VFC view)
            if (vfcCount === 1) {
              const v = receipt.vfcIds?.[0];
              onView(receipt, { vfcId: v?._id || v, vfcName: v?.vfcName || "VFC" });
            } else {
              onView(receipt, null);
            }
          }} />
          {perVfcList.length > 1 && (
            <button onClick={() => setExpanded(e => !e)} style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "7px 12px",
              background: expanded ? "#EBF8FF" : "#F7FAFC",
              color: expanded ? "#2B6CB0" : "#4A5568",
              border: `1px solid ${expanded ? "#BEE3F8" : "#E2E8F0"}`,
              borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>
              <i className={`mdi mdi-${expanded ? "chevron-up" : "chevron-down"}`} style={{ fontSize: 15 }} />
              Per VFC ({perVfcList.length})
            </button>
          )}
        </div>
      </div>

      {/* ── Per-VFC rows ── */}
      {expanded && perVfcList.length > 0 && (
        <div style={{ borderTop: "1px solid #EDF2F7", background: "#F8FAFC", padding: "12px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
            Individual VFC Receipts
          </div>
          {perVfcList.map((vr, i) => (
            <div key={vr.vfcId || i} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 14px", gap: 12,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: "#E8F5E9", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="mdi mdi-tree" style={{ color: "#2E7D32", fontSize: 14 }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#1A202C", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {vr.vfcName || "VFC"}
                  </div>
                  <div style={{ fontSize: 10, color: "#A0AEC0" }}>₹ {perVfcTotal.toLocaleString("en-IN")} · GST incl.</div>
                </div>
              </div>
              <ViewBtn label="Receipt" small onClick={() => onView(receipt, { vfcId: vr.vfcId, vfcName: vr.vfcName })} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RazorpayPayments = () => {
  const Api = process.env.REACT_APP_API;
  const [rangeData,       setRangeData]       = useState(null);
  const [receipts,        setReceipts]        = useState([]);
  const [selectedFY,      setSelectedFY]      = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [paying,          setPaying]          = useState(false);
  const [showManual,      setShowManual]      = useState(false);
  const [transactionId,   setTransactionId]   = useState("");
  const [file,            setFile]            = useState(null);
  const [submitting,      setSubmitting]      = useState(false);
  const [activeReceipt,   setActiveReceipt]   = useState(null);   // { receipt, vfcFilter? }


  const fetchStatus = useCallback(async () => {
    setLoading(true);

    // ── Payment status (critical — page depends on this) ──────────────────
    try {
      const statusRes = await axios.get(
        `${Api}/api/check/service-charge-status`,
        Authorization()
      );
      setRangeData(statusRes.data[0] || null);
    } catch (err) {
      console.error("service-charge-status error:", err);
    }

    // ── Receipts (non-critical — page works without them) ─────────────────
    try {
      const receiptsRes = await axios.get(
        `${Api}/api/check/my-receipts`,
        Authorization()
      );
      const allReceipts = groupReceipts(receiptsRes.data || []);
      setReceipts(allReceipts);
      if (allReceipts.length > 0) {
        const fyList = [...new Set(allReceipts.map(r => r.financialYear))].sort().reverse();
        setSelectedFY(prev => prev || fyList[0]);
      } else {
        setSelectedFY(prev => prev || getCurrentFY());
      }
    } catch (err) {
      console.error("my-receipts error:", err);
      setSelectedFY(prev => prev || getCurrentFY());
    }

    setLoading(false);
  }, [Api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const openPayment = async () => {
    // Use only UNPAID vfcIds
    const vfcIds     = rangeData.unpaidVfcIds;
    if (!vfcIds?.length) return;
    const totalAmount = vfcIds.length * TOTAL_PER_VFC;
    setPaying(true);
    try {
      const res = await axios.post(
        `${Api}/api/razorpay/create-order`,
        { amount: totalAmount, vfcIds, totalAmount },
        Authorization()
      );
      const { id: order_id, amount, keyId } = res.data;
      new window.Razorpay({
        key:      keyId,
        amount,
        currency: "INR",
        name:     "VFC Annual Fee",
        order_id,
        theme:    { color: "#2E7D32" },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${Api}/api/razorpay/verify-payment`,
              {
                order_id,
                payment_id: response.razorpay_payment_id,
                signature:  response.razorpay_signature,
              },
              Authorization()
            );
            toast.success("Payment successful! Receipt is ready.");
            await fetchStatus();
            // Auto-open the receipt modal for this payment
            const saved = verifyRes.data?.payment;
            if (saved) {
              setActiveReceipt({ receipt: saved, vfcFilter: null });
            }
          } catch {
            toast.error("Payment verification failed. Contact support.");
          }
        },
        modal: {
          ondismiss: () => {
            axios.post(`${Api}/api/razorpay/payment-failed`, { orderId: order_id }, Authorization()).catch(() => {});
          },
        },
      }).open();
    } catch (err) {
      console.error(err);
      toast.error("Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  const submitManualPayment = async () => {
    if (!transactionId.trim()) { toast.error("Please enter the transaction ID"); return; }
    if (!file)                  { toast.error("Please upload the payment proof");  return; }
    const vfcIds   = rangeData.unpaidVfcIds;
    const formData = new FormData();
    formData.append("transactionId", transactionId);
    formData.append("totalAmount",   vfcIds.length * TOTAL_PER_VFC);
    formData.append("file", file);
    vfcIds.forEach(id => formData.append("vfcIds[]", id));
    setSubmitting(true);
    try {
      await axios.post(`${Api}/api/check/manual-payment`, formData, Authorization());
      toast.success("Payment proof submitted successfully!");
      setShowManual(false);
      setTransactionId("");
      setFile(null);
      fetchStatus();
    } catch (err) {
      console.error(err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Loading ── */
  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300, gap: 14 }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", border: "4px solid #E2E8F0", borderTopColor: "#2E7D32", animation: "spin 0.8s linear infinite" }} />
      <div style={{ fontSize: 13, color: "#A0AEC0" }}>Loading payment status…</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  /* ── Empty state ── */
  if (!rangeData) return (
    <div>
      <PageHeader />
      <div style={{
        background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14,
        padding: "60px 24px", textAlign: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", background: "#F7FAFC",
          display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px",
        }}>
          <i className="mdi mdi-cash-remove" style={{ fontSize: 32, color: "#CBD5E0" }} />
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#2D3748", marginBottom: 8 }}>No Payment Record Found</div>
        <div style={{ fontSize: 13, color: "#718096", maxWidth: 400, margin: "0 auto" }}>
          No service charge record is linked to your account. Please contact your administrator.
        </div>
      </div>
    </div>
  );

  const unpaidVfcIds  = rangeData.unpaidVfcIds || [];
  const totalAmount   = unpaidVfcIds.length * TOTAL_PER_VFC;
  const isFullyPaid   = rangeData.isFullyPaid;

  return (
    <div style={{ paddingBottom: 40 }}>
      <PageHeader />

      {/* ── Status banner ── */}
      {isFullyPaid ? (
        <div style={{
          background: "#E8F5E9", border: "1px solid #C8E6C9", borderRadius: 12,
          padding: "14px 20px", marginBottom: 24,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <i className="mdi mdi-check-circle" style={{ color: "#2E7D32", fontSize: 22, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1B5E20" }}>All payments completed</div>
            <div style={{ fontSize: 12, color: "#388E3C" }}>Your range has fully paid the annual service charge for this financial year.</div>
          </div>
        </div>
      ) : (
        <div style={{
          background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 12,
          padding: "14px 20px", marginBottom: 24,
          display: "flex", alignItems: "flex-start", gap: 12,
        }}>
          <i className="mdi mdi-alert-circle" style={{ color: "#F59E0B", fontSize: 22, flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#78350F", marginBottom: 4 }}>Annual Service Charge Required</div>
            <div style={{ fontSize: 12, color: "#92400E", lineHeight: 1.7 }}>
              As per department guidelines, an <strong>annual service charge</strong> must be paid for all active VFCs.
              Payment is required <strong>once per financial year</strong> and a GST receipt will be generated.
              Unpaid VFCs may restrict loan processing and reporting.
            </div>
          </div>
        </div>
      )}

      {/* ── Stat cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        <StatCard icon="map-marker"   label="Range"      value={rangeData.rangeName}  accent="#2E7D32" />
        <StatCard icon="domain"       label="Total VFCs" value={rangeData.totalVfcs}   accent="#1976D2" />
        <StatCard icon="check-circle" label="Paid"       value={rangeData.paidVfcs}    accent="#388E3C" sub={`${rangeData.paidVfcs} VFCs paid`} />
        <StatCard icon="clock-alert"  label="Pending"    value={rangeData.unpaidVfcs}  accent="#D97706" sub={`${rangeData.unpaidVfcs} VFCs unpaid`} />
      </div>

      {/* ── Two-column layout: fee breakdown + action ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>

        {/* Fee breakdown */}
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 8 }}>
            <i className="mdi mdi-calculator-variant" style={{ color: "#2E7D32", fontSize: 18 }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748" }}>Fee Breakdown</div>
          </div>
          <div style={{ padding: "16px 20px" }}>
            {[
              { label: "Base Amount per VFC",  value: `₹ ${PER_VFC_AMOUNT}`,                          color: "#2D3748" },
              { label: "GST @ 18%",            value: `₹ ${Math.round(PER_VFC_AMOUNT * GST)}`,        color: "#D97706" },
              { label: "Total per VFC",        value: `₹ ${TOTAL_PER_VFC}`,                           color: "#1B5E20" },
              { label: "VFCs to be paid",      value: rangeData.unpaidVfcs,                            color: "#2D3748" },
            ].map((row) => (
              <div key={row.label} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0", borderBottom: "1px solid #F0F4F7",
              }}>
                <span style={{ fontSize: 13, color: "#4A5568" }}>{row.label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: row.color }}>{row.value}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 14, marginTop: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#1A202C" }}>Total Amount Due</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: "#1B5E20" }}>
                ₹ {totalAmount.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>

        {/* Payment action */}
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 8 }}>
            <i className="mdi mdi-credit-card-outline" style={{ color: "#2E7D32", fontSize: 18 }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748" }}>Make Payment</div>
          </div>
          <div style={{ padding: "24px 20px", display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Online pay */}
            <button
              onClick={openPayment}
              disabled={paying || isFullyPaid}
              style={{
                display: "flex", alignItems: "center", gap: 12, padding: "14px 20px",
                background: (paying || isFullyPaid) ? "#A5D6A7" : "linear-gradient(135deg,#1B5E20,#2E7D32)",
                color: "#fff", border: "none", borderRadius: 10,
                fontSize: 14, fontWeight: 700, cursor: (paying || isFullyPaid) ? "not-allowed" : "pointer",
                boxShadow: "0 4px 14px rgba(46,125,50,0.35)", textAlign: "left", width: "100%",
              }}
            >
              <div style={{ width: 38, height: 38, borderRadius: 9, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="mdi mdi-cash" style={{ fontSize: 20 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  {paying ? "Processing…" : isFullyPaid ? "Payment Complete" : "Pay Online"}
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 2 }}>
                  {isFullyPaid ? "All VFCs paid for this year" : "Secure payment via Razorpay · GST receipt generated"}
                </div>
              </div>
              {!paying && !isFullyPaid && (
                <div style={{ marginLeft: "auto", fontSize: 15, fontWeight: 800 }}>
                  ₹ {totalAmount.toLocaleString("en-IN")}
                </div>
              )}
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, height: 1, background: "#E2E8F0" }} />
              <span style={{ fontSize: 11, color: "#A0AEC0", fontWeight: 600 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: "#E2E8F0" }} />
            </div>

            {/* Manual payment */}
            <button
              onClick={() => setShowManual(true)}
              disabled={isFullyPaid}
              style={{
                display: "flex", alignItems: "center", gap: 12, padding: "14px 20px",
                background: "#fff",
                color: isFullyPaid ? "#A0AEC0" : "#2D3748",
                border: `1.5px solid ${isFullyPaid ? "#E2E8F0" : "#CBD5E0"}`,
                borderRadius: 10, fontSize: 14, fontWeight: 600,
                cursor: isFullyPaid ? "not-allowed" : "pointer",
                textAlign: "left", width: "100%",
              }}
              onMouseEnter={(e) => { if (!isFullyPaid) { e.currentTarget.style.borderColor = "#2E7D32"; e.currentTarget.style.background = "#F0FFF4"; } }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#CBD5E0"; e.currentTarget.style.background = "#fff"; }}
            >
              <div style={{ width: 38, height: 38, borderRadius: 9, background: "#F0FFF4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="mdi mdi-bank-transfer" style={{ fontSize: 20, color: "#2E7D32" }} />
              </div>
              <div>
                <div>Upload Bank / Manual Payment</div>
                <div style={{ fontSize: 11, color: "#A0AEC0", marginTop: 2 }}>Already paid via bank transfer? Upload proof</div>
              </div>
            </button>

            <p style={{ margin: 0, fontSize: 11, color: "#A0AEC0", textAlign: "center", lineHeight: 1.6 }}>
              <i className="mdi mdi-shield-check" style={{ color: "#2E7D32", marginRight: 4 }} />
              Payments are processed securely. A GST receipt is generated automatically.
            </p>
          </div>
        </div>
      </div>

      {/* ── Receipt History (all financial years) ── */}
      {(() => {
        // Build sorted FY list (newest first)
        const fyList = [...new Set(receipts.map(r => r.financialYear))].sort().reverse();
        const activeFY = selectedFY || getCurrentFY();
        const fyReceipts = receipts.filter(r => r.financialYear === activeFY);
        const fyTotal = fyReceipts.reduce((s, r) => s + (r.totalAmount || 0), 0);

        return (
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>

            {/* Header */}
            <div style={{
              padding: "14px 20px", borderBottom: "1px solid #E2E8F0",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-receipt" style={{ color: "#2E7D32", fontSize: 18 }} />
                <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748" }}>
                  Payment Receipts &amp; History
                </div>
                <span style={{
                  background: "#E8F5E9", color: "#2E7D32",
                  fontSize: 11, fontWeight: 700,
                  borderRadius: 20, padding: "2px 9px",
                  border: "1px solid #C8E6C9",
                }}>{receipts.length} total</span>
              </div>
              <button
                onClick={fetchStatus}
                style={{
                  background: "none", border: "1px solid #E2E8F0", borderRadius: 7,
                  color: "#718096", cursor: "pointer", padding: "5px 10px",
                  fontSize: 12, display: "flex", alignItems: "center", gap: 5,
                }}
              >
                <i className="mdi mdi-refresh" style={{ fontSize: 14 }} />
                Refresh
              </button>
            </div>

            {receipts.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#A0AEC0" }}>
                <i className="mdi mdi-receipt-outline" style={{ fontSize: 36, display: "block", marginBottom: 10 }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: "#718096" }}>No receipts yet</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Receipts will appear here after a successful payment.</div>
              </div>
            ) : (
              <>
                {/* ── Financial Year Tabs ── */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 0,
                  borderBottom: "1px solid #E2E8F0",
                  padding: "0 20px", overflowX: "auto",
                }}>
                  {fyList.map(fy => {
                    const count   = receipts.filter(r => r.financialYear === fy).length;
                    const isActive = fy === activeFY;
                    return (
                      <button
                        key={fy}
                        onClick={() => setSelectedFY(fy)}
                        style={{
                          background: "none",
                          border: "none",
                          borderBottom: isActive ? "2px solid #2E7D32" : "2px solid transparent",
                          color: isActive ? "#1B5E20" : "#718096",
                          fontWeight: isActive ? 700 : 500,
                          fontSize: 13,
                          padding: "11px 16px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          display: "flex", alignItems: "center", gap: 6,
                          marginBottom: -1,
                          transition: "color 0.15s",
                        }}
                      >
                        FY {fy}
                        <span style={{
                          background: isActive ? "#E8F5E9" : "#F7FAFC",
                          color:      isActive ? "#2E7D32" : "#A0AEC0",
                          fontSize: 10, fontWeight: 700,
                          borderRadius: 20, padding: "1px 7px",
                          border: `1px solid ${isActive ? "#C8E6C9" : "#E2E8F0"}`,
                        }}>{count}</span>
                      </button>
                    );
                  })}
                </div>

                {/* ── FY Summary strip ── */}
                <div style={{
                  background: "#F8FAFC", borderBottom: "1px solid #E2E8F0",
                  padding: "10px 20px",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <div style={{ fontSize: 12, color: "#718096" }}>
                    <strong style={{ color: "#2D3748" }}>{fyReceipts.length}</strong> payment{fyReceipts.length !== 1 ? "s" : ""} in FY {activeFY}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#1B5E20" }}>
                    Total: ₹ {fyTotal.toLocaleString("en-IN")}
                  </div>
                </div>

                {/* ── Receipt cards for active FY ── */}
                <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {fyReceipts.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "24px 0", color: "#A0AEC0" }}>
                      <div style={{ fontSize: 13 }}>No payments found for FY {activeFY}.</div>
                    </div>
                  ) : (
                    fyReceipts.map(r => (
                      <ReceiptCard
                        key={r._id}
                        receipt={r}
                        onView={(rec, vfcFilter) => setActiveReceipt({ receipt: rec, vfcFilter })}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* ── Receipt Modal ── */}
      {activeReceipt && (
        <ReceiptModal
          receipt={activeReceipt.receipt}
          vfcFilter={activeReceipt.vfcFilter}
          onClose={() => setActiveReceipt(null)}
        />
      )}

      {/* ── Manual Payment Modal ── */}
      {showManual && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,20,30,0.65)",
          backdropFilter: "blur(4px)", zIndex: 1060,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460,
            boxShadow: "0 24px 80px rgba(0,0,0,0.3)", overflow: "hidden",
          }}>
            <div style={{
              background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
              padding: "16px 20px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <i className="mdi mdi-bank-transfer" style={{ color: "#fff", fontSize: 20 }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Upload Payment Proof</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Bank transfer / NEFT / UPI</div>
                </div>
              </div>
              <button onClick={() => setShowManual(false)} style={{
                background: "rgba(255,255,255,0.15)", border: "none",
                borderRadius: 7, color: "#fff", cursor: "pointer",
                width: 30, height: 30, fontSize: 16,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>✕</button>
            </div>

            <div style={{ padding: 24 }}>
              <div style={{
                background: "#F8FAFC", border: "1px solid #E2E8F0",
                borderRadius: 10, padding: "12px 16px", marginBottom: 20,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div style={{ fontSize: 12, color: "#718096" }}>
                  <div style={{ fontWeight: 600, color: "#2D3748", marginBottom: 2 }}>{rangeData.rangeName}</div>
                  {unpaidVfcIds.length} VFC(s) × ₹{TOTAL_PER_VFC}
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1B5E20" }}>
                  ₹ {totalAmount.toLocaleString("en-IN")}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Transaction / Reference ID <span style={{ color: "#E53E3E" }}>*</span></label>
                <input
                  type="text"
                  placeholder="e.g. TXN123456789"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = "#2E7D32"}
                  onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Payment Proof (Screenshot / Receipt) <span style={{ color: "#E53E3E" }}>*</span></label>
                <label style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  border: `2px dashed ${file ? "#2E7D32" : "#CBD5E0"}`,
                  borderRadius: 10, padding: "20px 16px", cursor: "pointer",
                  background: file ? "#F0FFF4" : "#FAFAFA", transition: "all 0.15s",
                }}>
                  <i className={`mdi mdi-${file ? "check-circle" : "cloud-upload-outline"}`}
                    style={{ fontSize: 28, color: file ? "#2E7D32" : "#A0AEC0", marginBottom: 8 }} />
                  <span style={{ fontSize: 12, color: file ? "#1B5E20" : "#718096", fontWeight: 600 }}>
                    {file ? file.name : "Click to upload or drag & drop"}
                  </span>
                  {!file && <span style={{ fontSize: 11, color: "#A0AEC0", marginTop: 4 }}>PNG, JPG, PDF up to 5MB</span>}
                  <input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files[0])} style={{ display: "none" }} />
                </label>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={submitManualPayment}
                  disabled={submitting}
                  style={{
                    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    padding: "11px 20px",
                    background: submitting ? "#A5D6A7" : "linear-gradient(135deg,#1B5E20,#2E7D32)",
                    color: "#fff", border: "none", borderRadius: 9,
                    fontSize: 13, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer",
                    boxShadow: "0 3px 10px rgba(46,125,50,0.3)",
                  }}
                >
                  {submitting
                    ? <><i className="mdi mdi-loading mdi-spin" /> Submitting…</>
                    : <><i className="mdi mdi-check" /> Submit Proof</>
                  }
                </button>
                <button
                  onClick={() => setShowManual(false)}
                  style={{
                    padding: "11px 20px", background: "#fff", color: "#4A5568",
                    border: "1px solid #E2E8F0", borderRadius: 9,
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Page header ── */
const isTestMode = process.env.REACT_APP_RAZORPAY_MODE !== "prod";

const PageHeader = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
    <div style={{
      width: 42, height: 42, borderRadius: 11,
      background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <i className="mdi mdi-cash-multiple" style={{ color: "#fff", fontSize: 22 }} />
    </div>
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1A202C" }}>Annual Maintenance Fee</h4>
        {isTestMode && (
          <span style={{
            background: "#FFF3CD", color: "#856404",
            border: "1px solid #FFE08A",
            fontSize: 10, fontWeight: 700, borderRadius: 20,
            padding: "2px 9px", letterSpacing: 0.4,
          }}>
            TEST MODE
          </span>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 12, color: "#718096" }}>
        <span style={{ color: "#2E7D32" }}>VFC</span>
        {" / "}
        <span style={{ color: "#2E7D32" }}>JFMC Maintenance Fee</span>
        {" / "}
        <span style={{ color: "#2D3748", fontWeight: 600 }}>Pay Fee</span>
      </p>
    </div>
  </div>
);

export default RazorpayPayments;
